import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, ArrowRight, ArrowLeft, ArrowUp, ArrowDown } from 'lucide-react'

interface TourTooltipProps {
  targetSelector?: string
  title: string
  content: string
  currentStep: number
  totalSteps: number
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  onClose: () => void
  showBack?: boolean
  isLastStep?: boolean
  nextButtonText?: string
}

const TourTooltip: React.FC<TourTooltipProps> = ({
  targetSelector,
  title,
  content,
  currentStep,
  totalSteps,
  position = 'bottom',
  onNext,
  onBack,
  onSkip,
  onClose,
  showBack = true,
  isLastStep = false,
  nextButtonText
}) => {
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({})
  const [arrowPosition, setArrowPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('bottom')
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let retryCount = 0
    const maxRetries = 10 // Limit retries to prevent infinite loops
    let retryTimeout: NodeJS.Timeout

    const calculatePosition = () => {
      if (!targetSelector || position === 'center') {
        // Center of screen
        setTooltipStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10001
        })
        setArrowStyle({ display: 'none' })
        return
      }

      const targetElement = document.querySelector(targetSelector)
      if (!targetElement || !tooltipRef.current) {
        // Retry finding the element after a delay (DOM might not be ready yet after tab switch)
        if (retryCount < maxRetries) {
          retryCount++
          const delay = 100
          retryTimeout = setTimeout(calculatePosition, delay)
          console.log(`🔍 Tour: Waiting for element ${targetSelector} (attempt ${retryCount}/${maxRetries})`)
          // Show centered tooltip while waiting
          setTooltipStyle({
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10001
          })
          return
        }
        // If target not found after retries, notify parent to skip this step
        console.warn(`⚠️ Tour: Could not find element ${targetSelector} after ${maxRetries} attempts - skipping step`)
        onNext() // Auto-advance to next step instead of showing misplaced tooltip
        return
      }

      // Element found! Log success
      console.log(`✅ Tour: Found element ${targetSelector}`)
      retryCount = 0 // Reset for next time

      // Highlight target element immediately
      targetElement.classList.add('tour-highlight')

      // Scroll element into view FIRST before calculating position
      targetElement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' })

      // Use requestAnimationFrame to ensure browser has completed scroll and layout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!tooltipRef.current) return

          const targetRect = targetElement.getBoundingClientRect()
          const tooltipRect = tooltipRef.current.getBoundingClientRect()
          const spacing = 24

          let top = 0
          let left = 0

          switch (position) {
            case 'bottom':
              top = targetRect.bottom + spacing
              left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
              setArrowPosition('top')
              break
            case 'top':
              top = targetRect.top - tooltipRect.height - spacing
              left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
              setArrowPosition('bottom')
              break
            case 'left':
              top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2)
              left = targetRect.left - tooltipRect.width - spacing
              setArrowPosition('right')
              break
            case 'right':
              top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2)
              left = targetRect.right + spacing
              setArrowPosition('left')
              break
          }

          // Keep within viewport
          if (left < 10) left = 10
          if (left + tooltipRect.width > window.innerWidth - 10) {
            left = window.innerWidth - tooltipRect.width - 10
          }
          if (top < 10) top = 10
          if (top + tooltipRect.height > window.innerHeight - 10) {
            top = window.innerHeight - tooltipRect.height - 10
          }

          setTooltipStyle({
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            zIndex: 10001
          })
        })
      })
    }

    calculatePosition()
    window.addEventListener('resize', calculatePosition)
    window.addEventListener('scroll', calculatePosition, true)

    return () => {
      clearTimeout(retryTimeout)
      window.removeEventListener('resize', calculatePosition)
      window.removeEventListener('scroll', calculatePosition, true)

      // Remove highlight
      if (targetSelector) {
        const targetElement = document.querySelector(targetSelector)
        if (targetElement) {
          targetElement.classList.remove('tour-highlight')
        }
      }
    }
  }, [targetSelector, position])

  const ArrowIcon = () => {
    switch (arrowPosition) {
      case 'top':
        return <ArrowUp className="h-8 w-8 text-blue-600" />
      case 'bottom':
        return <ArrowDown className="h-8 w-8 text-blue-600" />
      case 'left':
        return <ArrowLeft className="h-8 w-8 text-blue-600" />
      case 'right':
        return <ArrowRight className="h-8 w-8 text-blue-600" />
    }
  }

  const tooltipContent = (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-60 z-10000 backdrop-blur-sm"
        onClick={onSkip}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-fadeIn border border-gray-100"
      >
        {/* Progress dots */}
        <div className="flex justify-center gap-2 pt-4 px-6">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i + 1 === currentStep
                  ? 'w-8 bg-blue-600'
                  : i + 1 < currentStep
                  ? 'w-2 bg-blue-400'
                  : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">
              Step {currentStep} of {totalSteps}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* SECURITY FIX: Removed dangerouslySetInnerHTML to prevent XSS attacks
              Previous code allowed arbitrary HTML which could inject malicious JavaScript
              Now safely renders content as plain text, preserving line breaks */}
          <div className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">
            {content}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-b-2xl border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onSkip}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Skip Tour
          </button>

          <div className="flex items-center gap-3">
            {showBack && currentStep > 1 && (
              <button
                onClick={onBack}
                className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              >
                Back
              </button>
            )}
            <button
              onClick={onNext}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40"
            >
              {nextButtonText || (isLastStep ? 'Finish Tour' : 'Next')}
            </button>
          </div>
        </div>

        {/* Animated arrow pointing to target */}
        {targetSelector && position !== 'center' && (
          <div
            className={`absolute pointer-events-none ${
              arrowPosition === 'top'
                ? '-top-10 left-1/2 -translate-x-1/2'
                : arrowPosition === 'bottom'
                ? '-bottom-10 left-1/2 -translate-x-1/2'
                : arrowPosition === 'left'
                ? '-left-10 top-1/2 -translate-y-1/2'
                : '-right-10 top-1/2 -translate-y-1/2'
            } animate-pulse`}
          >
            <ArrowIcon />
          </div>
        )}
      </div>
    </>
  )

  return createPortal(tooltipContent, document.body)
}

export default TourTooltip
